import { createGlobalStyle } from 'styled-components';
import interRegularUrl from 'inter-ui/variable/InterVariable.woff2';
import interItalicUrl from 'inter-ui/variable/InterVariable-Italic.woff2';

export const STUDIO_ROOT_CLASS = 'vbs-studio-root';

/**
 * Studio renders into the element carrying `STUDIO_ROOT_CLASS` and portals the
 * rest to `document.body`: its own modal container, the design system's
 * `Portal` (dropdowns, popovers), react-laag's `#layers` (tooltips, layered
 * menus) and the design system's `ModalBase`, which portals to `document.body`
 * under a `b-modal-<id>` root unless a `container` is passed. Baseline rules are
 * scoped to those so they never restyle a host page the custom element is
 * embedded in. `:where()` keeps their specificity at zero, so any component
 * rule overrides them.
 */
const STUDIO_SCOPE = `:where(.${STUDIO_ROOT_CLASS}, #vbs-modal-container, .bynder-design-system-portal, #layers, [data-floating-ui-portal], [class*='b-modal-'])`;

export const GlobalStyles = createGlobalStyle`
    /* Same font files as inter-ui/variable, registered under the name "Inter" like the design system does. */
    @font-face {
        font-family: Inter;
        font-style: normal;
        font-weight: 100 900;
        font-display: swap;
        src: url('${interRegularUrl}') format('woff2');
    }

    @font-face {
        font-family: Inter;
        font-style: italic;
        font-weight: 100 900;
        font-display: swap;
        src: url('${interItalicUrl}') format('woff2');
    }

    ${STUDIO_SCOPE} {
        font-family: Inter, 'Source Sans 3', 'Source Sans Pro', SourceSansPro, sans-serif;
        /* px, not rem: an embedding page may set its own root font size (e.g. html { font-size: 10px }). */
        font-size: 14px;
        font-weight: 400;
        line-height: 1.5;
        color: #334e5c;
        text-align: left;
        -webkit-text-size-adjust: 100%;
        -webkit-tap-highlight-color: transparent;
    }

    ${STUDIO_SCOPE},
    ${STUDIO_SCOPE} *,
    ${STUDIO_SCOPE} *::before,
    ${STUDIO_SCOPE} *::after {
        box-sizing: border-box;
    }

    /*
     * The pages were laid out against a document at least this wide. This used to sit on
     * body; it now sits on Studio's own root so an embedding page keeps its own body.
     */
    .${STUDIO_ROOT_CLASS} {
        min-width: 1200px;
    }

    /* Every page body that scrolls its own content (also read by pages/utils.ts). */
    ${STUDIO_SCOPE} #scroll-wrapper {
        height: 100%;
        overflow-y: hidden;
    }

    /* Typography */
    ${STUDIO_SCOPE} :is(h1, h2, h3, h4, h5, h6) {
        margin-top: 0;
        margin-bottom: 10px;
        font-weight: 600;
        line-height: 1.2;
    }

    ${STUDIO_SCOPE} h1 {
        font-size: 35px;
    }

    ${STUDIO_SCOPE} h2 {
        font-size: 28px;
    }

    ${STUDIO_SCOPE} h3 {
        font-size: 24.5px;
    }

    ${STUDIO_SCOPE} h4 {
        font-size: 21px;
    }

    ${STUDIO_SCOPE} h5 {
        font-size: 17.5px;
    }

    ${STUDIO_SCOPE} h6 {
        font-size: 14px;
    }

    ${STUDIO_SCOPE} p {
        margin-top: 0;
        margin-bottom: 16px;
    }

    ${STUDIO_SCOPE} :is(ol, ul, dl) {
        margin-top: 0;
        margin-bottom: 16px;
    }

    ${STUDIO_SCOPE} :is(ol ol, ul ul, ol ul, ul ol) {
        margin-bottom: 0;
    }

    ${STUDIO_SCOPE} dt {
        font-weight: 600;
    }

    ${STUDIO_SCOPE} dd {
        margin-bottom: 8px;
        margin-left: 0;
    }

    ${STUDIO_SCOPE} blockquote {
        margin: 0 0 16px;
    }

    ${STUDIO_SCOPE} :is(b, strong) {
        font-weight: bolder;
    }

    ${STUDIO_SCOPE} small {
        font-size: 0.875em;
        font-weight: 400;
    }

    ${STUDIO_SCOPE} :is(sub, sup) {
        position: relative;
        font-size: 75%;
        line-height: 0;
        vertical-align: baseline;
    }

    ${STUDIO_SCOPE} sub {
        bottom: -0.25em;
    }

    ${STUDIO_SCOPE} sup {
        top: -0.5em;
    }

    ${STUDIO_SCOPE} a {
        color: #334e5c;
        text-decoration: none;
        background-color: transparent;
    }

    ${STUDIO_SCOPE} a:hover {
        color: #18242b;
        text-decoration: underline;
    }

    ${STUDIO_SCOPE} a:not([href]):not([class]),
    ${STUDIO_SCOPE} a:not([href]):not([class]):hover {
        color: inherit;
        text-decoration: none;
    }

    ${STUDIO_SCOPE} :is(pre, code, kbd, samp) {
        font-family: SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
        font-size: 1em;
    }

    ${STUDIO_SCOPE} pre {
        margin-top: 0;
        margin-bottom: 16px;
        overflow: auto;
        white-space: normal;
    }

    ${STUDIO_SCOPE} hr {
        box-sizing: content-box;
        height: 0;
        margin-top: 20px;
        margin-bottom: 20px;
        overflow: visible;
        border: 0;
        border-top: 1px solid rgba(0, 34, 51, 0.1019607843);
    }

    /* Media and tables */
    ${STUDIO_SCOPE} figure {
        margin: 0 0 16px;
    }

    ${STUDIO_SCOPE} img {
        vertical-align: middle;
        border-style: none;
    }

    ${STUDIO_SCOPE} svg {
        overflow: hidden;
        vertical-align: baseline;
    }

    ${STUDIO_SCOPE} table {
        border-collapse: collapse;
    }

    ${STUDIO_SCOPE} caption {
        padding-top: 12px;
        padding-bottom: 12px;
        color: #809199;
        text-align: left;
        caption-side: bottom;
    }

    ${STUDIO_SCOPE} th {
        text-align: inherit;
    }

    /* Form controls */
    ${STUDIO_SCOPE} label {
        display: inline-block;
        margin-bottom: 8px;
    }

    ${STUDIO_SCOPE} button {
        border-radius: 0;
    }

    ${STUDIO_SCOPE} button:focus:not(:focus-visible) {
        outline: 0;
    }

    ${STUDIO_SCOPE} :is(input, button, select, optgroup, textarea) {
        margin: 0;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
    }

    ${STUDIO_SCOPE} :is(button, input) {
        overflow: visible;
    }

    ${STUDIO_SCOPE} :is(button, select) {
        text-transform: none;
    }

    ${STUDIO_SCOPE} :is(button, [type='button'], [type='reset'], [type='submit']) {
        -webkit-appearance: button;
    }

    ${STUDIO_SCOPE} :is(button, [type='button'], [type='reset'], [type='submit']):not(:disabled),
    ${STUDIO_SCOPE} [role='button'] {
        cursor: pointer;
    }

    ${STUDIO_SCOPE} input:is([type='radio'], [type='checkbox']) {
        box-sizing: border-box;
        padding: 0;
    }

    ${STUDIO_SCOPE} textarea {
        overflow: auto;
        resize: vertical;
    }

    ${STUDIO_SCOPE} fieldset {
        min-width: 0;
        padding: 0;
        margin: 0;
        border: 0;
    }

    ${STUDIO_SCOPE} legend {
        display: block;
        width: 100%;
        max-width: 100%;
        padding: 0;
        margin-bottom: 8px;
        font-size: 24px;
        line-height: inherit;
        color: inherit;
        white-space: normal;
    }

    ${STUDIO_SCOPE} progress {
        vertical-align: baseline;
    }

    ${STUDIO_SCOPE} [type='search'] {
        outline-offset: -2px;
        -webkit-appearance: none;
    }

    ${STUDIO_SCOPE} output {
        display: inline-block;
    }

    ${STUDIO_SCOPE} summary {
        display: list-item;
        cursor: pointer;
    }

    ${STUDIO_SCOPE} template {
        display: none;
    }

    ${STUDIO_SCOPE} [hidden] {
        display: none !important;
    }
`;
